import React from 'react';
import { useParams, Link } from 'react-router-dom';

const CaseStudyDetailPage = () => {
  const { id } = useParams();

  const caseStudies = {
    'retail-pos-transformation': {
      id: 'retail-pos-transformation',
      title: 'Retail POS Transformation',
      client: 'Al-Manar Retail Group',
      category: 'Retail',
      industry: 'Multi-location Retail Chain',
      location: 'Saudi Arabia',
      duration: '4 months',
      heroImage: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1200&h=600&fit=crop',
      results: [
        { metric: '65%', label: 'Faster Checkout' },
        { metric: '99.9%', label: 'System Uptime' },
        { metric: '40%', label: 'Inventory Accuracy Improvement' }
      ],
      technologies: ['Odoo 17', 'Odoo POS', 'Odoo Inventory', 'Odoo Accounting', 'PostgreSQL', 'Linux Server'],
      overview: 'Al-Manar Retail Group operates 12 retail stores across Saudi Arabia, ranging from convenience stores to large supermarkets. They were struggling with outdated point-of-sale systems that couldn\'t communicate with each other, leading to inventory discrepancies, slow checkout times, and no real-time visibility into sales performance.',
      challenge: {
        title: 'The Challenge',
        description: 'Al-Manar Retail Group faced several critical business challenges that were impacting their growth and profitability:',
        points: [
          'Legacy POS systems from different vendors across 12 locations with no integration',
          'Manual inventory counts taking 3 days per store, with 25% discrepancy rates',
          'No real-time sales data — managers had to wait until end of day for reports',
          'Customer checkout times averaging 4+ minutes during peak hours',
          'Frequent system crashes during high-traffic periods, resulting in lost sales',
          'Inability to run promotions consistently across all stores',
          'No customer loyalty program due to disconnected systems'
        ]
      },
      solution: {
        title: 'Our Solution',
        description: 'We implemented a comprehensive Odoo-based retail solution that unified all 12 stores under a single, powerful platform:',
        phases: [
          {
            phase: 'Phase 1: Assessment & Planning',
            duration: '2 weeks',
            activities: [
              'Conducted on-site audits of all 12 locations',
              'Documented existing workflows and pain points',
              'Designed unified data model for products, customers, and transactions',
              'Created migration strategy for historical data'
            ]
          },
          {
            phase: 'Phase 2: Core Implementation',
            duration: '6 weeks',
            activities: [
              'Deployed Odoo 17 Enterprise on high-availability cloud infrastructure',
              'Configured Odoo POS with offline mode for uninterrupted operations',
              'Set up real-time inventory synchronization across all locations',
              'Integrated barcode scanning and receipt printing',
              'Implemented multi-currency and VAT compliance for Saudi regulations'
            ]
          },
          {
            phase: 'Phase 3: Advanced Features',
            duration: '4 weeks',
            activities: [
              'Built custom loyalty program with points accumulation and redemption',
              'Created automated reorder rules based on sales velocity',
              'Developed real-time dashboards for management',
              'Implemented employee time tracking and commission calculations',
              'Set up promotional campaigns with automatic discounting'
            ]
          },
          {
            phase: 'Phase 4: Training & Go-Live',
            duration: '4 weeks',
            activities: [
              'Trained 120+ staff across all locations',
              'Phased rollout starting with 3 pilot stores',
              'Provided on-site support during first week of go-live',
              'Fine-tuned performance based on real usage patterns'
            ]
          }
        ]
      },
      results_detailed: {
        title: 'The Results',
        description: 'The transformation delivered measurable improvements across every aspect of Al-Manar\'s retail operations:',
        metrics: [
          {
            category: 'Operational Efficiency',
            items: [
              { metric: '65%', description: 'Reduction in average checkout time (from 4+ minutes to 1.4 minutes)' },
              { metric: '90%', description: 'Reduction in inventory count time (from 3 days to 4 hours per store)' },
              { metric: '40%', description: 'Improvement in inventory accuracy' }
            ]
          },
          {
            category: 'Business Performance',
            items: [
              { metric: '23%', description: 'Increase in sales within first 6 months' },
              { metric: '35%', description: 'Reduction in stockouts due to automated reordering' },
              { metric: '18%', description: 'Increase in customer retention through loyalty program' }
            ]
          },
          {
            category: 'Technical Performance',
            items: [
              { metric: '99.9%', description: 'System uptime with offline POS capability' },
              { metric: '<2s', description: 'Real-time sync across all 12 locations' },
              { metric: '0', description: 'System crashes since go-live' }
            ]
          }
        ]
      },
      testimonial: {
        quote: 'Nurrevo didn\'t just implement a POS system — they transformed how we run our entire retail operation. For the first time, I can see exactly what\'s happening across all 12 stores in real-time. Our checkout lines are faster, our inventory is accurate, and our customers are happier.',
        author: 'Mohammed Al-Harbi',
        role: 'CEO, Al-Manar Retail Group'
      }
    },
    'car-rental-fleet-management': {
      id: 'car-rental-fleet-management',
      title: 'Car Rental Fleet Management System',
      client: 'Desert Drive Car Rental',
      category: 'Automotive',
      industry: 'Car Rental & Fleet Management',
      location: 'UAE & Saudi Arabia',
      duration: '5 months',
      heroImage: 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=1200&h=600&fit=crop',
      results: [
        { metric: '45%', label: 'Higher Fleet Utilization' },
        { metric: '60%', label: 'Faster Booking Process' },
        { metric: '30%', label: 'Revenue Increase' }
      ],
      technologies: ['Odoo 17', 'Odoo Fleet', 'Odoo Website', 'Odoo CRM', 'Odoo Accounting', 'Custom Modules', 'REST API'],
      overview: 'Desert Drive Car Rental operates a fleet of 850+ vehicles across 15 locations in the UAE and Saudi Arabia. They were managing their operations through a combination of spreadsheets, a basic booking website, and manual processes. This resulted in double-bookings, maintenance delays, and poor customer experience.',
      challenge: {
        title: 'The Challenge',
        description: 'Desert Drive was facing operational chaos that threatened their competitive position in a rapidly growing market:',
        points: [
          'Double-bookings occurring 3-4 times weekly due to disconnected systems',
          'No visibility into vehicle availability across locations in real-time',
          'Maintenance schedules tracked in spreadsheets, leading to missed services',
          'Customer booking process took 15+ minutes with manual paperwork',
          'No integration between website bookings and internal operations',
          'Damage tracking and deposit management done manually with frequent disputes',
          'Insurance documentation scattered across multiple systems',
          'No automated billing — invoices created manually for each rental'
        ]
      },
      solution: {
        title: 'Our Solution',
        description: 'We built a complete car rental management ecosystem using Odoo as the foundation, with custom modules tailored to the rental industry:',
        phases: [
          {
            phase: 'Phase 1: Discovery & Architecture',
            duration: '3 weeks',
            activities: [
              'Mapped entire rental workflow from booking to return',
              'Analyzed fleet data and categorized 850+ vehicles',
              'Designed custom rental management module architecture',
              'Planned integration with existing website and payment systems'
            ]
          },
          {
            phase: 'Phase 2: Core Rental System',
            duration: '8 weeks',
            activities: [
              'Developed custom Odoo Rental module with availability calendar',
              'Built real-time fleet tracking with location-based inventory',
              'Created automated pricing engine with dynamic rates',
              'Implemented digital contract signing with e-signature',
              'Set up multi-location vehicle transfers and availability sync'
            ]
          },
          {
            phase: 'Phase 3: Fleet & Maintenance',
            duration: '4 weeks',
            activities: [
              'Configured Odoo Fleet with custom rental extensions',
              'Built automated maintenance scheduling based on mileage and time',
              'Created damage inspection workflow with photo documentation',
              'Integrated fuel tracking and cost management',
              'Set up insurance expiry alerts and document management'
            ]
          },
          {
            phase: 'Phase 4: Customer Experience',
            duration: '4 weeks',
            activities: [
              'Built new booking website integrated with Odoo backend',
              'Created customer portal for booking management',
              'Implemented automated SMS/email notifications',
              'Developed mobile app for quick check-in/check-out',
              'Set up loyalty program with tier-based benefits'
            ]
          },
          {
            phase: 'Phase 5: Analytics & Optimization',
            duration: '2 weeks',
            activities: [
              'Built real-time dashboards for fleet utilization',
              'Created revenue analytics by vehicle category and location',
              'Implemented predictive maintenance alerts',
              'Set up automated reporting for management'
            ]
          }
        ]
      },
      results_detailed: {
        title: 'The Results',
        description: 'The new system transformed Desert Drive from an operationally challenged company into a market leader:',
        metrics: [
          {
            category: 'Fleet Performance',
            items: [
              { metric: '45%', description: 'Increase in fleet utilization rate' },
              { metric: '0', description: 'Double-bookings since go-live (down from 3-4 weekly)' },
              { metric: '28%', description: 'Reduction in vehicle downtime through proactive maintenance' }
            ]
          },
          {
            category: 'Customer Experience',
            items: [
              { metric: '60%', description: 'Faster booking process (from 15 minutes to 6 minutes)' },
              { metric: '4.7/5', description: 'Customer satisfaction rating (up from 3.2)' },
              { metric: '85%', description: 'Customers now book online vs. in-person' }
            ]
          },
          {
            category: 'Business Growth',
            items: [
              { metric: '30%', description: 'Increase in revenue within first year' },
              { metric: '40%', description: 'Reduction in administrative overhead' },
              { metric: '52%', description: 'Increase in repeat customer bookings' }
            ]
          }
        ]
      },
      testimonial: {
        quote: 'Before Nurrevo, we were drowning in spreadsheets and constantly apologizing for double-bookings. Now, our entire operation runs like clockwork. The system they built handles everything from the moment a customer books online to when they return the car. Our revenue is up 30% and our team can actually focus on customers instead of paperwork.',
        author: 'Fatima Al-Rashid',
        role: 'Operations Director, Desert Drive Car Rental'
      }
    },
    'manufacturing-erp-implementation': {
      id: 'manufacturing-erp-implementation',
      title: 'Manufacturing ERP Implementation',
      client: 'Precision Steel Industries',
      category: 'Manufacturing',
      industry: 'Steel Manufacturing & Fabrication',
      location: 'Oman',
      duration: '7 months',
      heroImage: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=1200&h=600&fit=crop',
      results: [
        { metric: '35%', label: 'Production Efficiency Gain' },
        { metric: '50%', label: 'Inventory Cost Reduction' },
        { metric: '80%', label: 'Faster Order Processing' }
      ],
      technologies: ['Odoo 17', 'Odoo Manufacturing', 'Odoo MRP', 'Odoo Quality', 'Odoo Maintenance', 'Odoo Purchase', 'Barcode Integration', 'IoT Sensors'],
      overview: 'Precision Steel Industries is a mid-sized steel fabrication company with 200+ employees, operating a 50,000 sqm facility in Oman. They produce custom steel structures, pipes, and components for the construction and oil & gas industries. Their operations were managed through a patchwork of legacy systems and manual processes.',
      challenge: {
        title: 'The Challenge',
        description: 'Precision Steel was facing severe operational inefficiencies that impacted their ability to compete for large contracts:',
        points: [
          'Production planning done manually in spreadsheets with no visibility into capacity',
          'Raw material inventory frequently incorrect — either overstocked or causing production delays',
          'No traceability of materials from purchase to finished product (critical for oil & gas clients)',
          'Quality control records maintained on paper, making audits extremely difficult',
          'Work orders communicated verbally, leading to errors and delays',
          'No real-time visibility into production progress for customer inquiries',
          'Costing estimates often inaccurate, impacting profitability on jobs',
          'Maintenance done reactively, causing unexpected production stoppages'
        ]
      },
      solution: {
        title: 'Our Solution',
        description: 'We implemented a comprehensive Odoo Manufacturing solution that digitized and optimized their entire production operation:',
        phases: [
          {
            phase: 'Phase 1: Process Mapping & Analysis',
            duration: '4 weeks',
            activities: [
              'Documented all manufacturing processes and workflows',
              'Analyzed Bill of Materials for 2,000+ products',
              'Mapped quality control requirements for different industries',
              'Identified bottlenecks and inefficiencies in current operations',
              'Designed new optimized workflows in Odoo'
            ]
          },
          {
            phase: 'Phase 2: Core Manufacturing Setup',
            duration: '8 weeks',
            activities: [
              'Configured Odoo Manufacturing with work centers and routings',
              'Built comprehensive BOM structure with variants',
              'Set up MRP for automated production scheduling',
              'Implemented barcode-based shop floor tracking',
              'Created work order system with tablet interfaces for operators'
            ]
          },
          {
            phase: 'Phase 3: Inventory & Procurement',
            duration: '5 weeks',
            activities: [
              'Implemented lot/serial tracking for full traceability',
              'Set up automated reorder rules for raw materials',
              'Built vendor management with quality ratings',
              'Created receiving inspection workflow',
              'Implemented warehouse locations with bin management'
            ]
          },
          {
            phase: 'Phase 4: Quality & Compliance',
            duration: '4 weeks',
            activities: [
              'Built quality control checkpoints throughout production',
              'Created digital inspection forms with photo documentation',
              'Implemented non-conformance tracking and resolution workflow',
              'Set up material test certificates management',
              'Built audit-ready reporting for ISO and client requirements'
            ]
          },
          {
            phase: 'Phase 5: Maintenance & IoT',
            duration: '4 weeks',
            activities: [
              'Configured preventive maintenance schedules for all equipment',
              'Integrated IoT sensors for real-time machine monitoring',
              'Built predictive maintenance alerts based on usage patterns',
              'Created spare parts inventory management',
              'Implemented maintenance work order system'
            ]
          },
          {
            phase: 'Phase 6: Costing & Analytics',
            duration: '3 weeks',
            activities: [
              'Configured accurate job costing with labor, materials, and overhead',
              'Built real-time production dashboards',
              'Created profitability analysis by product and customer',
              'Implemented KPI tracking for OEE and other metrics',
              'Set up automated management reports'
            ]
          }
        ]
      },
      results_detailed: {
        title: 'The Results',
        description: 'The Odoo implementation transformed Precision Steel into a digitally-enabled, efficient manufacturing operation:',
        metrics: [
          {
            category: 'Production Efficiency',
            items: [
              { metric: '35%', description: 'Improvement in overall production efficiency' },
              { metric: '60%', description: 'Reduction in production planning time' },
              { metric: '25%', description: 'Decrease in production cycle time' }
            ]
          },
          {
            category: 'Inventory & Materials',
            items: [
              { metric: '50%', description: 'Reduction in raw material inventory costs' },
              { metric: '95%', description: 'Inventory accuracy (up from 70%)' },
              { metric: '100%', description: 'Material traceability for compliance' }
            ]
          },
          {
            category: 'Business Operations',
            items: [
              { metric: '80%', description: 'Faster order processing and quoting' },
              { metric: '40%', description: 'Reduction in unplanned downtime' },
              { metric: '22%', description: 'Improvement in gross margin through accurate costing' }
            ]
          }
        ]
      },
      testimonial: {
        quote: 'Nurrevo understood manufacturing. They didn\'t just configure software — they redesigned how we work. The shop floor tablets, the barcode tracking, the quality checkpoints... everything flows seamlessly now. We recently passed an ISO audit with zero findings, and our oil & gas clients are impressed with our traceability. This system has helped us win contracts we couldn\'t have competed for before.',
        author: 'Ahmed Al-Balushi',
        role: 'General Manager, Precision Steel Industries'
      }
    }
  };

  const study = caseStudies[id];

  if (!study) {
    return (
      <main className="flex-grow">
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Case Study Not Found</h1>
            <p className="text-gray-600 mb-8">The case study you're looking for doesn't exist.</p>
            <Link to="/case-studies" className="inline-flex items-center gap-2 text-gray-900 font-semibold">
              <span className="material-symbols-outlined">arrow_back</span>
              Back to Case Studies
            </Link>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="flex-grow">
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white">
        <div className="absolute inset-0">
          <img 
            src={study.heroImage} 
            alt={study.title}
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <Link to="/case-studies" className="inline-flex items-center gap-2 text-gray-300 hover:text-white mb-8 transition-colors">
            <span className="material-symbols-outlined">arrow_back</span>
            Back to Case Studies
          </Link>
          <div className="max-w-4xl">
            <div className="flex flex-wrap gap-3 mb-6">
              <span className="px-3 py-1 bg-white/10 text-sm font-medium">{study.category}</span>
              <span className="px-3 py-1 bg-white/10 text-sm font-medium">{study.industry}</span>
              <span className="px-3 py-1 bg-white/10 text-sm font-medium">{study.location}</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">{study.title}</h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">{study.overview}</p>
            <div className="flex flex-wrap gap-8">
              <div>
                <div className="text-sm text-gray-400 mb-1">Client</div>
                <div className="text-lg font-semibold">{study.client}</div>
              </div>
              <div>
                <div className="text-sm text-gray-400 mb-1">Duration</div>
                <div className="text-lg font-semibold">{study.duration}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Results */}
      <section className="py-16 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {study.results.map((result, index) => (
              <div key={index} className="text-center p-8 bg-gray-50">
                <div className="text-5xl font-bold text-gray-900 mb-2">{result.metric}</div>
                <div className="text-gray-600 font-medium">{result.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Used */}
      <section className="py-12 bg-gray-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Technologies:</span>
            {study.technologies.map((tech, index) => (
              <span key={index} className="px-4 py-2 bg-white border border-gray-200 text-sm font-medium text-gray-700">
                {tech}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Challenge Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">{study.challenge.title}</h2>
          <p className="text-lg text-gray-600 mb-8">{study.challenge.description}</p>
          <ul className="space-y-4">
            {study.challenge.points.map((point, index) => (
              <li key={index} className="flex items-start gap-4">
                <span className="flex-shrink-0 w-6 h-6 bg-gray-900 text-white flex items-center justify-center text-sm font-bold mt-0.5">
                  {index + 1}
                </span>
                <span className="text-gray-700">{point}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">{study.solution.title}</h2>
          <p className="text-lg text-gray-600 mb-12">{study.solution.description}</p>
          
          <div className="space-y-12">
            {study.solution.phases.map((phase, index) => (
              <div key={index} className="relative pl-8 border-l-2 border-gray-300">
                <div className="absolute -left-3 top-0 w-6 h-6 bg-gray-900 text-white flex items-center justify-center text-xs font-bold">
                  {index + 1}
                </div>
                <div className="mb-2">
                  <h3 className="text-xl font-bold text-gray-900">{phase.phase}</h3>
                  <span className="text-sm text-gray-500">{phase.duration}</span>
                </div>
                <ul className="space-y-2 mt-4">
                  {phase.activities.map((activity, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <span className="material-symbols-outlined text-gray-400 text-lg mt-0.5">check</span>
                      <span className="text-gray-700">{activity}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Results Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white overflow-hidden">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-gray-900 text-white text-sm font-semibold uppercase tracking-wider mb-4">
              Measurable Impact
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{study.results_detailed.title}</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{study.results_detailed.description}</p>
          </div>
          
          <div className="space-y-16">
            {study.results_detailed.metrics.map((category, index) => (
              <div key={index} className="relative">
                {/* Category Header with Icon */}
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-12 h-12 bg-gray-900 flex items-center justify-center text-white shadow-lg">
                    <span className="material-symbols-outlined text-2xl">
                      {index === 0 ? 'speed' : index === 1 ? 'trending_up' : 'memory'}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">{category.category}</h3>
                </div>
                
                {/* Metrics Cards with Hover Effects */}
                <div className="grid md:grid-cols-3 gap-6">
                  {category.items.map((item, idx) => (
                    <div 
                      key={idx} 
                      className="group relative bg-white p-8 border border-gray-100 hover:border-gray-300 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                    >
                      {/* Accent Line */}
                      <div className="absolute top-0 left-0 right-0 h-1 bg-gray-900 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
                      
                      {/* Metric Number with Animated Effect */}
                      <div className="relative mb-4">
                        <div className="text-5xl md:text-6xl font-bold text-gray-900 group-hover:text-gray-700 transition-colors">
                          {item.metric}
                        </div>
                        {/* Decorative Background */}
                        <div className="absolute -top-2 -left-2 text-8xl font-bold text-gray-100 -z-10 group-hover:text-gray-200 transition-colors">
                          {item.metric.replace(/[^0-9]/g, '').slice(0, 2) || '0'}
                        </div>
                      </div>
                      
                      {/* Description */}
                      <div className="text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors">
                        {item.description}
                      </div>
                      
                      {/* Arrow Icon */}
                      <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                        <span className="material-symbols-outlined text-gray-400">north_east</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          
          {/* Summary Bar */}
          <div className="mt-16 p-8 bg-gray-900 text-white">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <span className="material-symbols-outlined text-3xl text-gray-400">verified</span>
                <div>
                  <div className="text-lg font-semibold">Verified Results</div>
                  <div className="text-gray-400 text-sm">All metrics measured 6 months post-implementation</div>
                </div>
              </div>
              <Link 
                to="/contact" 
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-gray-900 font-semibold hover:bg-gray-100 transition-colors"
              >
                Get Similar Results
                <span className="material-symbols-outlined">arrow_forward</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="material-symbols-outlined text-6xl text-gray-600 mb-8">format_quote</span>
          <blockquote className="text-2xl md:text-3xl font-medium leading-relaxed mb-8">
            "{study.testimonial.quote}"
          </blockquote>
          <div>
            <div className="text-lg font-semibold">{study.testimonial.author}</div>
            <div className="text-gray-400">{study.testimonial.role}</div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Ready to Transform Your Business?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Let's discuss how we can help you achieve similar results with a tailored Odoo implementation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white bg-gray-900 shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
            >
              Start Your Project
              <span className="material-symbols-outlined text-xl ml-2">arrow_forward</span>
            </Link>
            <Link
              to="/case-studies"
              className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-gray-900 border border-gray-300 hover:bg-gray-100 transition-all duration-200"
            >
              View More Case Studies
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default CaseStudyDetailPage;
